import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Table, Card, Space, Tag, Input, Select, DatePicker, Button, Row, Col,
    Modal, Form, InputNumber, message, Tooltip, Typography, Progress, Tabs, Dropdown, Drawer, Descriptions, Alert, Spin
} from 'antd';
import {
    SearchOutlined, ReloadOutlined, WarningOutlined, CheckCircleOutlined,
    QuestionCircleOutlined, LinkOutlined, EditOutlined, HistoryOutlined,
    ExclamationCircleOutlined, DeleteOutlined, MailOutlined, DownloadOutlined, DisconnectOutlined,
    EyeOutlined, FileTextOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useSearchParams } from 'react-router-dom';
import settlementApi from '../../../api/settlementApi';
import { flightOperationApi } from '../../../api/flightOperationFlightApi';
import { useAuth } from '../../../hooks/useAuth';
import FlightLogRecognitionPanel from './FlightLogRecognition/FlightLogRecognitionPanel';
import FuelFormImageList from './FuelFormImage/FuelFormImageList';
import CollapsibleFilterBar from '../../../components/common/CollapsibleFilterBar';

dayjs.extend(utc);

const { RangePicker } = DatePicker;

const FUEL_REPORT_TAB_KEYS = ['list', 'image', 'flight-log'];

const MATCH_STATUS_MAP = {
    auto_matched: { color: 'green', label: '自动匹配' },
    manual_matched: { color: 'blue', label: '人工匹配' },
    unmatched: { color: 'default', label: '未匹配' },
};
const COMPLETENESS_MAP = {
    complete: { color: 'green', label: '完整' },
    partial: { color: 'orange', label: '部分' },
};
const STATUS_MAP = {
    parsed: { color: 'processing', label: '已解析' },
    needs_review: { color: 'warning', label: '需审核' },
    reviewed: { color: 'cyan', label: '已审核' },
    confirmed: { color: 'success', label: '已确认' },
};
const MSG_TYPE_MAP = {
    GRBU_SUMMARY: { label: 'GRBU', color: 'purple' },
    PUSH_BACK_REPORT: { label: 'PushBack', color: 'volcano' },
    MERGED: { label: '合并', color: 'blue' },
    OTHER: { label: '其他', color: 'default' },
};
const REVIEW_REASON_MAP = {
    merge_conflict: { label: '合并冲突', color: 'gold' },
    long_gap_backfill: { label: '长间隔回填', color: 'orange' },
    negative_fuel: { label: '负油量', color: 'red' },
};

const fmtNum = (v) => (v != null ? v.toLocaleString('en-US', { maximumFractionDigits: 1 }) : '-');
const getManualFields = (record) => {
    const history = record?.parse_raw?.edit_history;
    if (!history || !Array.isArray(history)) return new Set();
    const fields = new Set();
    history.forEach(entry => { Object.keys(entry.changes || {}).forEach(f => fields.add(f)); });
    return fields;
};
const ManualTag = () => <Tag color="cyan" style={{ fontSize: 8, lineHeight: '12px', height: 14, padding: '0 2px', marginLeft: 2, verticalAlign: 'middle' }}>手</Tag>;
const getInFuelDerived = (record) => record?.parse_raw?.in_fuel_kg_derived;
const DerivedTag = ({ derived }) => {
    if (!derived) return null;
    const title = (
        <Space direction="vertical" size={0}>
            <span>来源航班：{derived.source_flight_number || '-'}</span>
            <span>地面停场：{derived.gap_hours != null ? `${derived.gap_hours}h` : '-'}</span>
        </Space>
    );
    return (
        <Tooltip title={title}>
            <Tag color="orange" style={{ fontSize: 8, lineHeight: '12px', height: 14, padding: '0 2px', marginLeft: 2, verticalAlign: 'middle' }}>推</Tag>
        </Tooltip>
    );
};
const fmtFuel = (v) => {
    if (v == null) return <Typography.Text type="secondary">-</Typography.Text>;
    return <Typography.Text strong style={{ fontFamily: 'Courier New, monospace' }}>{v.toLocaleString('en-US', { maximumFractionDigits: 1 })}</Typography.Text>;
};
const fmtFuelWithManual = (field) => (v, record) => {
    if (v == null) return <Typography.Text type="secondary">-</Typography.Text>;
    const isManual = getManualFields(record).has(field);
    const derived = field === 'in_fuel_kg' ? getInFuelDerived(record) : null;
    return (
        <span style={{ whiteSpace: 'nowrap' }}>
            <Typography.Text strong style={{ fontFamily: 'Courier New, monospace' }}>{v.toLocaleString('en-US', { maximumFractionDigits: 1 })}</Typography.Text>
            {isManual && <ManualTag />}
            {derived && <DerivedTag derived={derived} />}
        </span>
    );
};
const OOOI_SOURCE_TAG = { settlement: { label: '结', color: 'blue' }, mvt: { label: '运', color: 'default' }, msg: { label: '报', color: 'orange' } };
const fmtTimeWithSource = (sourceField) => (_, record) => {
    const timeVal = record[sourceField.replace('_source', '')];
    const src = record[sourceField];
    if (!timeVal) return <Typography.Text type="secondary">-</Typography.Text>;
    const tag = OOOI_SOURCE_TAG[src];
    return (
        <span style={{ whiteSpace: 'nowrap' }}>
            <span style={{ fontFamily: 'Courier New, monospace' }}>{dayjs.utc(timeVal).format('HH:mm')}</span>
            {tag && <Tag color={tag.color} style={{ fontSize: 8, lineHeight: '12px', height: 14, padding: '0 2px', marginLeft: 2, verticalAlign: 'middle' }}>{tag.label}</Tag>}
        </span>
    );
};
const fmtDate = (d) => (d ? dayjs(d).format('MM-DD') : <Typography.Text type="secondary">-</Typography.Text>);

const EmailHtmlPreview = ({ html }) => (
    <iframe
        title="fuel-report-email-html"
        sandbox=""
        srcDoc={html}
        style={{
            width: '100%',
            minHeight: 360,
            border: 0,
            background: '#fff',
        }}
    />
);

// ─── Email Preview Modal (supports primary + secondary email) ─────
const EmailPreviewSingle = ({ emailId }) => {
    const [loading, setLoading] = useState(false);
    const [emailData, setEmailData] = useState(null);
    useEffect(() => {
        if (emailId) {
            setLoading(true);
            setEmailData(null);
            settlementApi.fuelReport.getEmailContent(emailId)
                .then(res => setEmailData(res.data || res))
                .catch(() => message.error('获取邮件内容失败'))
                .finally(() => setLoading(false));
        }
    }, [emailId]);
    if (loading) return <div style={{ textAlign: 'center', padding: 24 }}><Typography.Text type="secondary">加载中…</Typography.Text></div>;
    if (!emailData) return null;
    const e = emailData;
    return (
        <div>
            <div style={{ marginBottom: 12, padding: '10px 14px', background: '#fafafa', borderRadius: 8, fontSize: 13 }}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <div><Typography.Text type="secondary">主题：</Typography.Text><Typography.Text strong>{e.subject || '-'}</Typography.Text></div>
                    <div><Typography.Text type="secondary">发件人：</Typography.Text>{e.from_address || '-'}</div>
                    <div><Typography.Text type="secondary">收件人：</Typography.Text>{e.to_address || '-'}</div>
                    <div><Typography.Text type="secondary">时间：</Typography.Text>{e.created_at ? dayjs(e.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</div>
                </Space>
            </div>
            <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 16, maxHeight: 400, overflow: 'auto', backgroundColor: '#fff' }}>
                {e.body_text ? (
                    <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'Courier New, monospace', fontSize: 13, lineHeight: 1.6 }}>{e.body_text}</pre>
                ) : e.body_html ? (
                    <EmailHtmlPreview html={e.body_html} />
                ) : (
                    <Typography.Text type="secondary">无邮件正文</Typography.Text>
                )}
            </div>
        </div>
    );
};

const EmailPreviewTab = ({ record }) => {
    if (!record?.raw_email_id) return <Typography.Text type="secondary">暂无关联邮件</Typography.Text>;
    const hasBoth = record.raw_email_id && record.raw_email_id_secondary;
    if (hasBoth) {
        return (
            <Tabs size="small" items={[
                { key: 'primary', label: 'GRBU (主)', children: <EmailPreviewSingle emailId={record.raw_email_id} /> },
                { key: 'secondary', label: 'PushBack (补)', children: <EmailPreviewSingle emailId={record.raw_email_id_secondary} /> },
            ]} />
        );
    }
    return <EmailPreviewSingle emailId={record.raw_email_id} />;
};

const FlightMatchTab = ({ record, onChanged }) => {
    const [flightNumber, setFlightNumber] = useState('');
    const [dateRange, setDateRange] = useState(null);
    const [flights, setFlights] = useState([]);
    const [searching, setSearching] = useState(false);
    const [matchingId, setMatchingId] = useState(null);
    const [revoking, setRevoking] = useState(false);

    useEffect(() => {
        if (record) {
            setFlightNumber(record.flight_number || '');
            if (record.flight_date) {
                const d = dayjs(record.flight_date);
                if (d.isValid()) setDateRange([d.subtract(1, 'day'), d.add(1, 'day')]);
            } else {
                setDateRange(null);
            }
            setFlights([]);
            setMatchingId(null);
        }
    }, [record]);

    const handleSearch = async () => {
        if (!flightNumber.trim() && !dateRange) { message.warning('请输入航班号或选择日期范围'); return; }
        setSearching(true);
        try {
            const params = { page_size: 50 };
            if (flightNumber.trim()) params.flight_number = flightNumber.trim();
            if (dateRange?.[0]) params.start_date = dateRange[0].format('YYYY-MM-DD');
            if (dateRange?.[1]) params.end_date = dateRange[1].format('YYYY-MM-DD');
            const res = await flightOperationApi.getFlightOperations(params);
            const allItems = res?.data?.items || [];
            setFlights(allItems.filter(f => !EXCLUDED_STATUSES.has(f.status)));
            if (allItems.length === 0) message.info('未找到匹配航班');
        } catch { message.error('搜索航班失败'); }
        finally { setSearching(false); }
    };

    const handleSelect = async (flight, force = false) => {
        setMatchingId(flight.id);
        try {
            const res = await settlementApi.fuelReport.manualMatch(record.id, flight.id, force);
            const result = res?.data || res;
            if (result?.success === false && result.conflict) {
                Modal.confirm({
                    title: '关联冲突',
                    icon: <ExclamationCircleOutlined />,
                    content: (result.message || '该航班已被其他燃油记录关联') + ' 确认后将强制关联到当前记录。',
                    okText: '强制关联', cancelText: '取消', okButtonProps: { danger: true },
                    onOk: () => handleSelect(flight, true),
                });
                setMatchingId(null);
                return;
            }
            message.success(force ? '已强制关联航班' : `已关联航班 ${flight.flight_number}`);
            onChanged();
        } catch (err) { message.error('关联失败: ' + (err.response?.data?.detail || err.message)); }
        finally { setMatchingId(null); }
    };

    const handleRevoke = () => {
        Modal.confirm({
            title: '解除关联',
            icon: <ExclamationCircleOutlined />,
            content: '解除后该燃油记录将变为"未匹配"状态，需要重新关联航班。确认解除？',
            okText: '解除', cancelText: '取消', okButtonProps: { danger: true },
            onOk: async () => {
                setRevoking(true);
                try {
                    await settlementApi.fuelReport.revokeMatch(record.id);
                    message.success('已解除航班关联');
                    onChanged();
                } catch (err) { message.error('解除失败: ' + (err.response?.data?.detail || err.message)); }
                finally { setRevoking(false); }
            },
        });
    };

    const r = record || {};
    const snapshot = r.flight_info_snapshot || {};
    const isMatched = r.match_status === 'auto_matched' || r.match_status === 'manual_matched';
    const matchLabel = MATCH_STATUS_MAP[r.match_status] || { color: 'default', label: r.match_status || '-' };

    const searchCols = [
        { title: '航班号', dataIndex: 'flight_number', width: 90, render: (v) => <Typography.Text strong>{v}</Typography.Text> },
        { title: '日期', dataIndex: 'flight_date', width: 100, render: (v) => v ? dayjs(v).format('YYYY-MM-DD') : '-' },
        { title: '始发', dataIndex: 'departure_airport', width: 55 },
        { title: '目的', dataIndex: 'arrival_airport', width: 55 },
        { title: 'STD', dataIndex: 'std', width: 100, render: (v) => v ? <span style={{ fontFamily: 'Courier New, monospace' }}>{dayjs.utc(v).format('MM-DD HH:mm')}</span> : '-' },
        { title: 'ATD', dataIndex: 'atd', width: 100, render: (v) => v ? <span style={{ fontFamily: 'Courier New, monospace' }}>{dayjs.utc(v).format('MM-DD HH:mm')}</span> : '-' },
        { title: '机号', dataIndex: 'aircraft_reg', width: 80 },
        { title: '状态', dataIndex: 'status', width: 80, render: (v) => <Tag color={FLIGHT_STATUS_COLOR[v] || 'default'}>{v || '-'}</Tag> },
        {
            title: '操作', width: 70, fixed: 'right', align: 'center',
            render: (_, flight) => (
                <Button type="link" size="small" loading={matchingId === flight.id}
                    disabled={matchingId !== null} onClick={() => handleSelect(flight)}>选择</Button>
            ),
        },
    ];

    const fmtTs = (v) => v ? dayjs.utc(v).format('MM-DD HH:mm') : '-';
    const diffTag = (a, b) => {
        if (!a || !b || a === '-' || b === '-') return null;
        return String(a).trim() === String(b).trim()
            ? <Tag color="green" style={{ margin: 0, fontSize: 10, lineHeight: '16px', height: 18, padding: '0 4px' }}>一致</Tag>
            : <Tag color="red" style={{ margin: 0, fontSize: 10, lineHeight: '16px', height: 18, padding: '0 4px' }}>不一致</Tag>;
    };

    const fop = r.matched_flight || snapshot;
    const compareRows = isMatched ? [
        { label: '航班号', fuel: r.flight_number || '-', fop: fop.flight_number || '-' },
        { label: '日期', fuel: r.flight_date || '-', fop: fop.flight_date || '-' },
        { label: '始发', fuel: r.origin_iata || r.origin_icao || '-', fop: fop.departure_airport || '-' },
        { label: '目的', fuel: r.dest_iata || r.dest_icao || '-', fop: fop.arrival_airport || '-' },
        { label: '机号', fuel: r.aircraft_reg || '-', fop: fop.aircraft_reg || '-' },
        { label: 'OUT 时间 (UTC)', fuel: fmtTs(r.out_time), fop: fmtTs(fop.atd) },
        { label: 'OFF 时间 (UTC)', fuel: fmtTs(r.off_time), fop: fmtTs(fop.atd_icao) },
        { label: 'ON 时间 (UTC)', fuel: fmtTs(r.on_time), fop: fmtTs(fop.ata_icao) },
        { label: 'IN 时间 (UTC)', fuel: fmtTs(r.in_time), fop: fmtTs(fop.ata) },
    ] : [];

    const compareCols = [
        { title: '字段', dataIndex: 'label', width: 80, render: (v) => <Typography.Text type="secondary">{v}</Typography.Text> },
        { title: '燃油报文', dataIndex: 'fuel', width: 130, render: (v) => <Typography.Text strong style={{ fontFamily: 'Courier New, monospace' }}>{v}</Typography.Text> },
        { title: '航班运营 (FOP)', dataIndex: 'fop', width: 130, render: (v) => <Typography.Text strong style={{ fontFamily: 'Courier New, monospace' }}>{v}</Typography.Text> },
        { title: '对比', width: 70, align: 'center', render: (_, row) => diffTag(row.fuel, row.fop) },
    ];

    return (
        <div>
            {r.has_duplicate_match && (
                <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                    message="该航班被多条燃油记录关联，请检查是否存在重复匹配" />
            )}

            {isMatched ? (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <Space>
                            <Tag color={matchLabel.color}>{matchLabel.label}</Tag>
                            <Typography.Text type="secondary">
                                {r.match_status === 'auto_matched' ? '系统自动' : '人工关联'}
                                {' · '}ID: {r.flight_operation_id}
                            </Typography.Text>
                        </Space>
                        <Button danger size="small" icon={<DisconnectOutlined />} loading={revoking} onClick={handleRevoke}>解除关联</Button>
                    </div>
                    <Table columns={compareCols} dataSource={compareRows.map((row, i) => ({ ...row, key: i }))}
                        size="small" pagination={false} bordered style={{ marginBottom: 16 }}
                        rowClassName={(row) => {
                            if (!row.fuel || !row.fop || row.fuel === '-' || row.fop === '-') return '';
                            return String(row.fuel).trim() !== String(row.fop).trim() ? 'fuel-compare-diff' : '';
                        }} />
                </>
            ) : (
                <Alert type="info" showIcon style={{ marginBottom: 16 }} message="当前未关联航班，请搜索并选择匹配航班。" />
            )}

            <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>{isMatched ? '重新关联航班' : '搜索匹配航班'}</Typography.Text>
            <Row gutter={8} style={{ marginBottom: 12 }} align="middle">
                <Col><Input placeholder="航班号" value={flightNumber} onChange={e => setFlightNumber(e.target.value)} onPressEnter={handleSearch} style={{ width: 130 }} allowClear /></Col>
                <Col><DatePicker.RangePicker value={dateRange} onChange={setDateRange} format="YYYY-MM-DD" style={{ width: 240 }} allowClear /></Col>
                <Col><Button type="primary" icon={<SearchOutlined />} loading={searching} onClick={handleSearch}>查询</Button></Col>
            </Row>
            <Table columns={searchCols} dataSource={flights} rowKey="id" size="small"
                scroll={{ x: 730, y: 260 }} pagination={false}
                locale={{ emptyText: '输入条件后点击查询' }} />
        </div>
    );
};

const FuelDetailDrawer = ({ record, open, onClose, onChanged, hasEditPermission }) => {
    const [approvingReview, setApprovingReview] = useState(false);
    const [editing, setEditing] = useState(false);
    useEffect(() => {
        setEditing(false);
    }, [record?.id, open]);
    if (!record) return null;
    const r = record;
    const matchLabel = MATCH_STATUS_MAP[r.match_status] || { color: 'default', label: r.match_status || '-' };
    const hasDuplicate = r.has_duplicate_match;
    const defaultTab = 'data';
    const parseRaw = r.parse_raw || {};
    const derived = parseRaw.in_fuel_kg_derived;
    const reviewReasons = Array.isArray(parseRaw.review_reasons) ? parseRaw.review_reasons : [];
    const hasMergeConflicts = parseRaw.merge_conflicts && Object.keys(parseRaw.merge_conflicts).length > 0;
    const hasReviewItems = reviewReasons.length > 0 || hasMergeConflicts;

    const handleClearReviewReason = async (reason) => {
        const meta = REVIEW_REASON_MAP[reason] || { label: reason };
        try {
            await settlementApi.fuelReport.clearReviewReason(r.id, reason);
            message.success(`已清除审核原因：${meta.label}`);
            onChanged();
        } catch (err) {
            message.error('清除审核原因失败: ' + (err.response?.data?.detail || err.message));
        }
    };

    const handleApproveReview = () => {
        Modal.confirm({
            title: '审核通过',
            icon: <CheckCircleOutlined />,
            content: '确认当前燃油报文数据可接受？系统将归档审核原因和合并冲突，并标记为已审核。',
            okText: '审核通过',
            cancelText: '取消',
            onOk: async () => {
                setApprovingReview(true);
                try {
                    await settlementApi.fuelReport.approveReview(r.id);
                    message.success('已审核通过');
                    onChanged();
                } catch (err) {
                    message.error('审核通过失败: ' + (err.response?.data?.detail || err.message));
                } finally {
                    setApprovingReview(false);
                }
            },
        });
    };

    const items = [
        {
            key: 'data',
            label: <Space size={4}><EditOutlined /> 数据</Space>,
            children: (
                <FuelEditPanel
                    record={r}
                    editing={editing}
                    hasEditPermission={hasEditPermission}
                    onEdit={() => setEditing(true)}
                    onCancelEdit={() => setEditing(false)}
                    onSaved={onChanged}
                />
            ),
        },
        r.raw_email_id && { key: 'email', label: <><MailOutlined /> 邮件原文</>, children: <EmailPreviewTab record={r} /> },
        {
            key: 'match',
            label: <Space size={4}>
                <LinkOutlined /> 航班匹配
                {hasDuplicate && <ExclamationCircleOutlined style={{ color: '#faad14' }} />}
            </Space>,
            children: <FlightMatchTab record={r} onChanged={onChanged} />,
        },
    ].filter(Boolean);

    return (
        <Drawer title={
            <Space>
                <Typography.Text strong>{r.flight_number || '-'}</Typography.Text>
                <Typography.Text type="secondary">{r.flight_date || '-'}</Typography.Text>
                <Typography.Text type="secondary">{r.aircraft_reg || '-'}</Typography.Text>
                <Tag color={matchLabel.color}>{matchLabel.label}</Tag>
                {(r.message_type && MSG_TYPE_MAP[r.message_type]) && <Tag color={MSG_TYPE_MAP[r.message_type].color}>{MSG_TYPE_MAP[r.message_type].label}</Tag>}
            </Space>
        }
            open={open} onClose={onClose} width={980} destroyOnClose>
            {(hasReviewItems || derived) && (
                <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: 12 }}>
                    {hasReviewItems && (
                        <Alert
                            type="warning"
                            showIcon
                            action={hasEditPermission ? (
                                <Button size="small" type="primary" loading={approvingReview} onClick={handleApproveReview}>
                                    审核通过
                                </Button>
                            ) : null}
                            message={
                                <Space wrap>
                                    <Typography.Text strong>审核原因</Typography.Text>
                                    {hasMergeConflicts && <Tag color="gold" style={{ margin: 0 }}>合并冲突</Tag>}
                                    {reviewReasons.map(reason => {
                                        const meta = REVIEW_REASON_MAP[reason] || { label: reason, color: 'default' };
                                        if (!hasEditPermission) {
                                            return <Tag key={reason} color={meta.color} style={{ margin: 0 }}>{meta.label}</Tag>;
                                        }
                                        return (
                                            <Tooltip key={reason} title="清除审核原因">
                                                <Tag
                                                    color={meta.color}
                                                    closable
                                                    style={{ margin: 0 }}
                                                    onClose={(e) => {
                                                        e.preventDefault();
                                                        handleClearReviewReason(reason);
                                                    }}
                                                >
                                                    {meta.label}
                                                </Tag>
                                            </Tooltip>
                                        );
                                    })}
                                </Space>
                            }
                        />
                    )}
                    {derived && (
                        <Card size="small" styles={{ body: { padding: '8px 12px' } }}>
                            <Descriptions size="small" column={2} title={<Space size={4}><Tag color="orange" style={{ margin: 0 }}>推</Tag><span>IN 油量推算来源</span></Space>}>
                                <Descriptions.Item label="来源航班">{derived.source_flight_number || '-'}</Descriptions.Item>
                                <Descriptions.Item label="来源记录">{derived.source_report_id || '-'}</Descriptions.Item>
                                <Descriptions.Item label="回填值">{derived.value_kg != null ? fmtNum(derived.value_kg) : '-'}</Descriptions.Item>
                                <Descriptions.Item label="地面停场">{derived.gap_hours != null ? `${derived.gap_hours}h` : '-'}</Descriptions.Item>
                                <Descriptions.Item label="标记">{(derived.flags || []).length ? derived.flags.map(flag => <Tag key={flag} color="orange">{flag}</Tag>) : '-'}</Descriptions.Item>
                                <Descriptions.Item label="时间">{derived.derived_at ? dayjs(derived.derived_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
                            </Descriptions>
                        </Card>
                    )}
                </Space>
            )}
            <Tabs defaultActiveKey={defaultTab} items={items} />
        </Drawer>
    );
};

// ─── Edit Modal ────────────────────────────────────────────────────
const FuelEditPanel = ({ record, editing, onEdit, onCancelEdit, onSaved, hasEditPermission }) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (record) {
            form.setFieldsValue({
                ...record,
                flight_date: record.flight_date ? dayjs(record.flight_date) : null,
            });
        }
    }, [record, form]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);
            if (values.flight_date) values.flight_date = values.flight_date.format('YYYY-MM-DD');
            const res = await settlementApi.fuelReport.update(record.id, values);
            const result = res.data || res;
            const changeCount = Object.keys(result.changes || {}).length;
            message.success(changeCount > 0 ? `已更新 ${changeCount} 个字段` : '无变更');
            onSaved();
        } catch (err) {
            if (err.errorFields) return;
            message.error('保存失败: ' + (err.response?.data?.detail || err.message));
        } finally {
            setSaving(false);
        }
    };

    const numProps = { style: { width: '100%' }, precision: 2 };
    const r = record || {};
    const parseRaw = r.parse_raw || {};
    const editHistory = parseRaw.edit_history || [];
    const mergeConflicts = parseRaw.merge_conflicts || {};

    const sectionCard = {
        background: '#fafbfc', borderRadius: 8, padding: '14px 20px 6px',
        marginBottom: 16, border: '1px solid #f0f0f0',
    };
    const sectionTitle = (color, text) => (
        <div style={{ fontSize: 13, fontWeight: 600, color: '#434343', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 3, height: 14, background: color, borderRadius: 2, flexShrink: 0 }} />
            {text}
        </div>
    );
    const compactItem = { marginBottom: 10 };
    const thirdCol = { xs: 24, md: 8, style: { minWidth: 0 } };
    const quarterCol = { xs: 24, sm: 12, lg: 6, style: { minWidth: 0 } };
    const halfCol = { xs: 24, md: 12, style: { minWidth: 0 } };

    return (
        <div>
            {hasEditPermission && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                    {editing ? (
                        <Space>
                            <Button onClick={onCancelEdit}>取消编辑</Button>
                            <Button type="primary" icon={<CheckCircleOutlined />} loading={saving} onClick={handleSave}>保存</Button>
                        </Space>
                    ) : (
                        <Button icon={<EditOutlined />} onClick={onEdit}>编辑数据</Button>
                    )}
                </div>
            )}

            <Tabs defaultActiveKey="edit" items={[
                { key: 'edit', label: editing ? '编辑数据' : '数据', children: (
                    <Form form={form} className="fuel-report-editor-form" layout="vertical" disabled={!editing} style={{ overflow: 'hidden' }}>
                        <div style={sectionCard}>
                            {sectionTitle('#1890ff', '航班信息')}
                            <Row gutter={16}>
                                <Col {...thirdCol}><Form.Item label="航班号" name="flight_number" style={compactItem}><Input placeholder="如 CT0166" /></Form.Item></Col>
                                <Col {...thirdCol}><Form.Item label="航班日期" name="flight_date" style={compactItem}><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
                                <Col {...thirdCol}><Form.Item label="机号" name="aircraft_reg" style={compactItem}><Input placeholder="如 9H-JJB" /></Form.Item></Col>
                            </Row>
                            <Row gutter={16}>
                                <Col {...quarterCol}><Form.Item label="始发 IATA" name="origin_iata" style={compactItem}><Input maxLength={3} placeholder="3位" /></Form.Item></Col>
                                <Col {...quarterCol}><Form.Item label="始发 ICAO" name="origin_icao" style={compactItem}><Input maxLength={4} placeholder="4位" /></Form.Item></Col>
                                <Col {...quarterCol}><Form.Item label="目的 IATA" name="dest_iata" style={compactItem}><Input maxLength={3} placeholder="3位" /></Form.Item></Col>
                                <Col {...quarterCol}><Form.Item label="目的 ICAO" name="dest_icao" style={compactItem}><Input maxLength={4} placeholder="4位" /></Form.Item></Col>
                            </Row>
                            <Row gutter={16}>
                                <Col {...halfCol}><Form.Item label="航司" name="operator" style={compactItem}><Input /></Form.Item></Col>
                                <Col {...halfCol}><Form.Item label="油单号" name="fuel_slip_number" style={compactItem}><Input /></Form.Item></Col>
                            </Row>
                        </div>

                        <Row gutter={16}>
                            <Col {...halfCol}>
                                <div style={sectionCard}>
                                    {sectionTitle('#722ed1', 'OOOI 油量 (kg)')}
                                    <Row gutter={12}>
                                        <Col {...halfCol}><Form.Item label="OUT" name="out_fuel_kg" style={compactItem}><InputNumber {...numProps} /></Form.Item></Col>
                                        <Col {...halfCol}><Form.Item label="OFF" name="off_fuel_kg" style={compactItem}><InputNumber {...numProps} /></Form.Item></Col>
                                    </Row>
                                    <Row gutter={12}>
                                        <Col {...halfCol}><Form.Item label="ON" name="on_fuel_kg" style={compactItem}><InputNumber {...numProps} /></Form.Item></Col>
                                        <Col {...halfCol}><Form.Item label="IN" name="in_fuel_kg" style={compactItem}><InputNumber {...numProps} /></Form.Item></Col>
                                    </Row>
                                </div>
                            </Col>
                            <Col {...halfCol}>
                                <div style={sectionCard}>
                                    {sectionTitle('#13c2c2', '加油信息')}
                                    <Row gutter={12}>
                                        <Col {...halfCol}><Form.Item label="加油(kg)" name="fuel_uplift_kg" style={compactItem}><InputNumber {...numProps} /></Form.Item></Col>
                                        <Col {...halfCol}><Form.Item label="加油(L)" name="fuel_uplift_liters" style={compactItem}><InputNumber {...numProps} /></Form.Item></Col>
                                    </Row>
                                    <Row gutter={12}>
                                        <Col {...halfCol}><Form.Item label="密度" name="fuel_density" style={compactItem}><InputNumber {...numProps} precision={4} /></Form.Item></Col>
                                        <Col {...halfCol}><Form.Item label="加油前(kg)" name="previous_fuel_kg" style={compactItem}><InputNumber {...numProps} /></Form.Item></Col>
                                    </Row>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                )},
                { key: 'history', label: `编辑记录${editHistory.length ? ` (${editHistory.length})` : ''}`, children: (
                    editHistory.length > 0 ? (
                        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                            {editHistory.slice().reverse().map((entry, idx) => (
                                <div key={idx} style={{ padding: '10px 14px', marginBottom: 8, background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                            {entry.at ? dayjs(entry.at).format('YYYY-MM-DD HH:mm:ss') : '-'}
                                        </Typography.Text>
                                    </div>
                                    {Object.entries(entry.changes || {}).map(([field, change]) => (
                                        <div key={field} style={{ fontSize: 13, marginBottom: 2 }}>
                                            <Typography.Text code style={{ fontSize: 12 }}>{field}</Typography.Text>
                                            {' '}
                                            <Typography.Text type="secondary" delete>{change.old ?? '空'}</Typography.Text>
                                            {' → '}
                                            <Typography.Text strong>{change.new ?? '空'}</Typography.Text>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <Typography.Text type="secondary">暂无编辑记录</Typography.Text>
                        </div>
                    )
                )},
                ...(r.message_type === 'MERGED' ? [{ key: 'merge', label: <span>{Object.keys(mergeConflicts).length > 0 && <WarningOutlined style={{ color: '#faad14', marginRight: 4 }} />}合并详情</span>, children: (
                    <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                        {Object.keys(mergeConflicts).length > 0 ? (
                            <>
                                <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fffbe6', borderRadius: 6, border: '1px solid #ffe58f' }}>
                                    <WarningOutlined style={{ color: '#faad14', marginRight: 6 }} />
                                    <Typography.Text type="warning" style={{ fontSize: 13 }}>
                                        以下字段在 GRBU 与 PushBack 中存在差异，请人工确认
                                    </Typography.Text>
                                </div>
                                <Table size="small" pagination={false} dataSource={Object.entries(mergeConflicts).map(([k, v]) => ({ key: k, field: k, ...v }))}
                                    columns={[
                                        { title: '字段', dataIndex: 'field', width: 140, render: (v) => <Typography.Text code>{v}</Typography.Text> },
                                        { title: 'GRBU 值', dataIndex: 'grbu', width: 160, render: (v) => <Typography.Text>{v ?? '-'}</Typography.Text> },
                                        { title: 'PushBack 值', dataIndex: 'pushback', width: 160, render: (v) => <Typography.Text>{v ?? '-'}</Typography.Text> },
                                    ]} />
                            </>
                        ) : (
                            <div style={{ textAlign: 'center', padding: 40 }}>
                                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18, marginRight: 6 }} />
                                <Typography.Text type="secondary">GRBU 与 PushBack 无冲突字段</Typography.Text>
                            </div>
                        )}
                        {parseRaw.grbu && parseRaw.pushback && (
                            <div style={{ marginTop: 16 }}>
                                <Typography.Text strong style={{ fontSize: 13, marginBottom: 8, display: 'block' }}>原始解析值对比</Typography.Text>
                                <Row gutter={12}>
                                    <Col span={12}>
                                        <div style={{ background: '#f6f0ff', borderRadius: 6, padding: '8px 12px', border: '1px solid #d3adf7' }}>
                                            <Typography.Text strong style={{ fontSize: 12, color: '#722ed1' }}>GRBU</Typography.Text>
                                            <pre style={{ fontSize: 11, margin: '4px 0 0', maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                                                {JSON.stringify(parseRaw.grbu, null, 2)}
                                            </pre>
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div style={{ background: '#fff2e8', borderRadius: 6, padding: '8px 12px', border: '1px solid #ffbb96' }}>
                                            <Typography.Text strong style={{ fontSize: 12, color: '#fa541c' }}>PushBack</Typography.Text>
                                            <pre style={{ fontSize: 11, margin: '4px 0 0', maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                                                {JSON.stringify(parseRaw.pushback, null, 2)}
                                            </pre>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </div>
                )}] : []),
            ]} />
        </div>
    );
};

// ─── Match Modal ───────────────────────────────────────────────────
const FLIGHT_STATUS_COLOR = { COMPLETED: 'green', ACTIVE: 'blue', SCHEDULED: 'default', DELAYED: 'orange' };
const EXCLUDED_STATUSES = new Set(['REPLACED', 'PLAN_CANCELLED', 'CANCELLED']);

// FlightMatchModal removed — functionality merged into FuelDetailDrawer → FlightMatchTab

// ─── Inner List Panel (原有"航班燃油明细"主面板) ──────────────────
const FuelReportListPanel = () => {
    const { hasPermission } = useAuth();
    const hasEditPermission = hasPermission('statistics:fuel:edit');

    const getDefaultDateRange = () => {
        const now = dayjs();
        return [now.startOf('month'), now.endOf('month')];
    };

    const LIST_PAGE_SIZE = 50;
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [listPage, setListPage] = useState(1);
    const listGenerationRef = useRef(0);
    const loadingRef = useRef(false);
    const loadingMoreRef = useRef(false);
    const hasMore = data.length < total;
    const [filters, setFilters] = useState({
        flight_number: '', aircraft_reg: '', operator: '', origin: '', dest: '',
        match_status: undefined, data_completeness: undefined, message_type: undefined,
        localDateRange: getDefaultDateRange(),
    });
    const [detailRecord, setDetailRecord] = useState(null);
    const [ferryToggling, setFerryToggling] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    // 机号/航司下拉选项：取自燃油报文实际数据（distinct），覆盖主数据没有的外租/包机机号
    const [regOptions, setRegOptions] = useState([]);
    const [operatorOptions, setOperatorOptions] = useState([]);
    // 全筛选范围聚合（v3 列表接口返回，不受分页影响）
    const [aggregates, setAggregates] = useState(null);
    const [reprocessOpen, setReprocessOpen] = useState(false);
    const [reprocessRange, setReprocessRange] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [reprocessing, setReprocessing] = useState(false);
    const [reprocessResult, setReprocessResult] = useState(null);
    const [backfillingInFuel, setBackfillingInFuel] = useState(false);
    const [emailDiagnosticsOpen, setEmailDiagnosticsOpen] = useState(false);
    const [emailDiagnosticsLoading, setEmailDiagnosticsLoading] = useState(false);
    const [emailDiagnostics, setEmailDiagnostics] = useState({ items: [], summary: null });
    const [emailReprocessingId, setEmailReprocessingId] = useState(null);
    const searchTimerRef = useRef(null);
    const tableWrapRef = useRef(null);
    const [tableScrollY, setTableScrollY] = useState(480);

    const buildFilterParams = (flt = filters) => {
        const params = {};
        const dr = flt.localDateRange;
        if (dr && dr[0]) params.date_from = typeof dr[0] === 'string' ? dr[0] : dr[0].format('YYYY-MM-DD');
        if (dr && dr[1]) params.date_to = typeof dr[1] === 'string' ? dr[1] : dr[1].format('YYYY-MM-DD');
        if (flt.flight_number) params.flight_number = flt.flight_number;
        if (flt.aircraft_reg) params.aircraft_reg = flt.aircraft_reg;
        if (flt.operator) params.operator = flt.operator;
        if (flt.origin) params.origin = flt.origin;
        if (flt.dest) params.dest = flt.dest;
        if (flt.match_status) params.match_status = flt.match_status;
        if (flt.data_completeness) params.data_completeness = flt.data_completeness;
        if (flt.message_type) params.message_type = flt.message_type;
        return params;
    };

    const fetchList = async ({ page = 1, reset = true, flt = filters } = {}) => {
        let generation;
        if (reset) {
            generation = listGenerationRef.current + 1;
            listGenerationRef.current = generation;
            loadingMoreRef.current = false;
            loadingRef.current = true;
            setLoading(true);
            setLoadingMore(false);
            setSelectedRowKeys([]);
        } else {
            if (loadingMoreRef.current || loadingRef.current) return;
            generation = listGenerationRef.current;
            if (generation === 0) return;
            loadingMoreRef.current = true;
            setLoadingMore(true);
        }

        try {
            const params = { page, page_size: LIST_PAGE_SIZE, ...buildFilterParams(flt) };
            const res = await settlementApi.fuelReport.getList(params);
            if (generation !== listGenerationRef.current) return;
            const result = res.data || res;
            const items = result.items || [];
            setData((prev) => (reset ? items : [...prev, ...items]));
            setTotal(result.total || 0);
            setListPage(page);
            if (reset) setAggregates(result.aggregates || null);
            if (reset) {
                requestAnimationFrame(() => {
                    const body = tableWrapRef.current?.querySelector('.ant-table-body');
                    if (body) body.scrollTop = 0;
                });
            }
        } catch (err) {
            if (generation !== listGenerationRef.current) return;
            console.error('获取燃油明细失败:', err);
        } finally {
            if (generation === listGenerationRef.current) {
                loadingRef.current = false;
                setLoading(false);
                setLoadingMore(false);
                loadingMoreRef.current = false;
            }
        }
    };

    const loadMore = () => {
        if (!hasMore || loadingRef.current || loadingMoreRef.current) return;
        fetchList({ page: listPage + 1, reset: false });
    };

    const handleTableScroll = (event) => {
        const target = event?.currentTarget || event?.target;
        if (!target || typeof target.scrollTop !== 'number') return;
        const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
        if (distanceToBottom < 100) {
            loadMore();
        }
    };

    useEffect(() => {
        fetchList({ page: 1, reset: true });
        settlementApi.fuelReport.getFilterOptions()
            .then((res) => {
                const result = res.data || res;
                setRegOptions(result.aircraft_regs || []);
                setOperatorOptions(result.operators || []);
            })
            .catch(() => { /* 选项加载失败不阻塞列表，退化为自由输入 */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = () => {
        fetchList({ page: 1, reset: true });
    };
    const handleReset = () => {
        const defaults = {
            flight_number: '', aircraft_reg: '', operator: '', origin: '', dest: '',
            match_status: undefined, data_completeness: undefined, message_type: undefined,
            localDateRange: getDefaultDateRange(),
        };
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        setFilters(defaults);
        fetchList({ page: 1, reset: true, flt: defaults });
    };

    // 摘要栏快捷筛选：文本输入防抖搜索，下拉即时搜索
    const runQuickSearch = (nextFilters, { immediate = false } = {}) => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        const doFetch = () => {
            fetchList({ page: 1, reset: true, flt: nextFilters });
        };
        if (immediate) {
            doFetch();
        } else {
            searchTimerRef.current = setTimeout(doFetch, 500);
        }
    };

    const handleDetailChanged = () => { setDetailRecord(null); fetchList({ page: 1, reset: true }); };
    const handleToggleFerry = async (record, newVal) => {
        setFerryToggling(record.id);
        try {
            await settlementApi.fuelReport.update(record.id, { is_ferry: newVal });
            message.success(`已标记为「${newVal === true ? '空飞' : newVal === false ? '载货' : '未知'}」`);
            fetchList({ page: 1, reset: true });
        } catch (err) {
            message.error('标记失败: ' + (err.response?.data?.detail || err.message));
        } finally {
            setFerryToggling(null);
        }
    };

    const handleDelete = (ids) => {
        const idList = Array.isArray(ids) ? ids : [ids];
        Modal.confirm({
            title: '确认删除',
            icon: <ExclamationCircleOutlined />,
            content: `将永久删除 ${idList.length} 条燃油报文记录，此操作不可恢复，是否继续？`,
            okText: '删除',
            okButtonProps: { danger: true },
            cancelText: '取消',
            onOk: async () => {
                try {
                    const res = await settlementApi.fuelReport.deleteRecords(idList);
                    const result = res.data || res;
                    message.success(`已删除 ${result.deleted} 条记录`);
                    setSelectedRowKeys([]);
                    fetchList({ page: 1, reset: true });
                } catch (err) {
                    const detail = err.response?.data?.detail;
                    const msg = typeof detail === 'string' ? detail : err.message;
                    message.error(`删除失败: ${msg}`);
                }
            },
        });
    };

    const handleBatchRematch = async (ids) => {
        const selectedIds = Array.isArray(ids) ? ids : [];
        const payload = selectedIds.length > 0
            ? { ids: selectedIds }
            : { filters: buildFilterParams(filters) };
        const label = selectedIds.length > 0 ? `${selectedIds.length} 条选中记录` : '当前筛选范围内的未匹配记录';
        Modal.confirm({
            title: '重新匹配航班',
            icon: <LinkOutlined />,
            content: `将对${label}重新执行自动匹配，是否继续？`,
            okText: '开始匹配',
            cancelText: '取消',
            onOk: async () => {
                try {
                    const res = await settlementApi.fuelReport.batchRematch(payload);
                    const r = res.data || res;
                    message.success(`匹配完成：${r.matched}/${r.total} 条成功`);
                    setSelectedRowKeys([]);
                    fetchList({ page: 1, reset: true });
                } catch (err) {
                    message.error('重新匹配失败: ' + (err.response?.data?.detail || err.message));
                }
            },
        });
    };

    const handleBackfillInFuel = () => {
        Modal.confirm({
            title: '回填 IN 油量',
            icon: <HistoryOutlined />,
            content: '将扫描已匹配航班的燃油记录，用下一班加油前存量回填缺失或已推算的 IN 油量。是否继续？',
            okText: '开始回填',
            cancelText: '取消',
            onOk: async () => {
                setBackfillingInFuel(true);
                try {
                    const res = await settlementApi.fuelReport.backfillInFuel();
                    const r = res.data || res;
                    const skippedText = Object.entries(r.skipped_reasons || {})
                        .map(([reason, count]) => `${reason}: ${count}`)
                        .join('，');
                    message.success(`回填完成：扫描 ${r.scanned || 0} 条，更新 ${r.updated || 0} 条${skippedText ? `，跳过 ${skippedText}` : ''}`, 6);
                    fetchList({ page: 1, reset: true });
                } catch (err) {
                    message.error('回填失败: ' + (err.response?.data?.detail || err.message));
                } finally {
                    setBackfillingInFuel(false);
                }
            },
        });
    };

    const fetchEmailDiagnostics = async () => {
        setEmailDiagnosticsLoading(true);
        try {
            const res = await settlementApi.fuelReport.diagnoseEmails({ days: 7, limit: 100 });
            setEmailDiagnostics(res.data || res);
        } catch (err) {
            message.error('邮件诊断加载失败: ' + (err.response?.data?.detail || err.message));
        } finally {
            setEmailDiagnosticsLoading(false);
        }
    };

    const openEmailDiagnostics = () => {
        setEmailDiagnosticsOpen(true);
        fetchEmailDiagnostics();
    };

    const handleReprocessEmail = async (rawEmailId) => {
        setEmailReprocessingId(rawEmailId);
        try {
            const res = await settlementApi.fuelReport.reprocessEmail(rawEmailId);
            const result = res.data || res;
            if (result.ok) {
                message.success(`重跑完成，生成/关联 ${result.fuel_report_ids?.length || 0} 条燃油报文`);
                fetchList({ page: 1, reset: true });
            } else {
                message.warning('重跑完成，但仍未生成燃油报文');
            }
            await fetchEmailDiagnostics();
        } catch (err) {
            message.error('重跑失败: ' + (err.response?.data?.detail || err.message));
        } finally {
            setEmailReprocessingId(null);
        }
    };

    const handleExport = async () => {
        if (total === 0) { message.warning('无数据可导出'); return; }
        if (total > 10000) {
            const confirmed = await new Promise((resolve) => {
                Modal.confirm({
                    title: '导出数据超过 10000 条',
                    icon: <ExclamationCircleOutlined />,
                    content: `当前筛选共有 ${total} 条记录，系统最多导出前 10000 条。建议缩小筛选范围；如果继续，Excel 会是不完整结果。`,
                    okText: '继续导出',
                    cancelText: '取消',
                    onOk: () => resolve(true),
                    onCancel: () => resolve(false),
                });
            });
            if (!confirmed) return;
        }
        setExporting(true);
        try {
            const params = buildFilterParams(filters);

            const res = await settlementApi.fuelReport.exportExcel(params);
            const result = res.data || res;
            if (result?.download_url) {
                const { handleExportDownload } = await import('../../../utils/exportUtils');
                await handleExportDownload(result.download_url, result.file_name || '燃油报文.xlsx');
                message.success('导出成功');
            } else {
                message.error('导出失败：未获取到下载链接');
            }
        } catch (err) {
            const detail = err.response?.data?.detail;
            const msg = detail === '无数据可导出' ? detail : (detail || err.message);
            message.error(`导出失败: ${msg}`);
        } finally {
            setExporting(false);
        }
    };

    const pollRef = useRef(null);

    const stopPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    };

    useEffect(() => () => { stopPolling(); if (searchTimerRef.current) clearTimeout(searchTimerRef.current); }, []);

    useEffect(() => {
        const el = tableWrapRef.current;
        if (!el) return undefined;
        const update = () => {
            // 预留两行分组表头 + 底部「加载更多」，让滚动发生在表格内部，表头始终可见
            const reserved = 140;
            setTableScrollY(Math.max(240, el.clientHeight - reserved));
        };
        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const handleReprocess = async () => {
        if (!reprocessRange || !reprocessRange[0] || !reprocessRange[1]) {
            message.warning('请选择回扫日期范围');
            return;
        }
        setReprocessing(true);
        setReprocessResult(null);
        try {
            const res = await settlementApi.fuelReport.batchReprocess(
                reprocessRange[0].format('YYYY-MM-DD'),
                reprocessRange[1].format('YYYY-MM-DD'),
            );
            const { task_id } = res.data || res;
            if (!task_id) throw new Error('未返回 task_id');

            pollRef.current = setInterval(async () => {
                try {
                    const statusRes = await settlementApi.fuelReport.getReprocessStatus(task_id);
                    const result = statusRes.data || statusRes;
                    setReprocessResult(result);

                    if (result.status === 'completed' || result.status === 'failed') {
                        stopPolling();
                        setReprocessing(false);
                        if (result.status === 'completed') {
                            if (result.created > 0) {
                                message.success(`回扫完成：新入库 ${result.created} 条`);
                                fetchList({ page: 1, reset: true });
                            } else {
                                message.info('回扫完成：无新数据');
                            }
                        } else {
                            message.error('回扫失败: ' + (result.error || '未知错误'));
                        }
                    }
                } catch {
                    stopPolling();
                    setReprocessing(false);
                    message.error('查询回扫状态失败');
                }
            }, 2000);
        } catch (err) {
            setReprocessing(false);
            message.error('回扫启动失败: ' + (err.response?.data?.detail || err.message));
        }
    };

    const fmtRoute = (r) => {
        const dep = r.origin_iata || r.origin_icao;
        const arr = r.dest_iata || r.dest_icao;
        if (!dep && !arr) return <Typography.Text type="secondary">--</Typography.Text>;
        return (
            <span>
                <Typography.Text strong style={{ color: '#1890ff' }}>{dep || '??'}</Typography.Text>
                <Typography.Text type="secondary"> → </Typography.Text>
                <Typography.Text strong style={{ color: '#1890ff' }}>{arr || '??'}</Typography.Text>
            </span>
        );
    };

    const columns = [
        {
            title: '类型', dataIndex: 'message_type', width: 80, fixed: 'left', align: 'center',
            render: (v, r) => {
                const t = MSG_TYPE_MAP[v] || { label: v, color: 'default' };
                const hasConflicts = r.parse_raw?.merge_conflicts && Object.keys(r.parse_raw.merge_conflicts).length > 0;
                return (
                    <Space size={2}>
                        <Tag color={t.color} style={{ margin: 0 }}>{t.label}</Tag>
                        {hasConflicts && (
                            <Tooltip title={`合并存在字段差异(${Object.keys(r.parse_raw.merge_conflicts).length}项)，需人工确认`}>
                                <WarningOutlined style={{ color: '#faad14', fontSize: 12 }} />
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: '航班号', dataIndex: 'flight_number', width: 80, fixed: 'left',
            render: (v) => v ? <Typography.Text strong>{v}</Typography.Text> : <Typography.Text type="secondary">-</Typography.Text>,
        },
        { title: '日期', dataIndex: 'flight_date', width: 62, align: 'center', render: fmtDate },
        {
            title: '机号', dataIndex: 'aircraft_reg', width: 78, align: 'center',
            render: (v) => v ? <Tag color="geekblue" style={{ margin: 0 }}>{v}</Tag> : '-',
        },
        { title: '航段', width: 100, render: (_, r) => fmtRoute(r) },
        {
            title: '空飞', dataIndex: 'is_ferry', width: 56, align: 'center',
            render: (v, r) => {
                const tag = v === true ? <Tag color="red" style={{ margin: 0 }}>空飞</Tag> : v === false ? <Tag color="green" style={{ margin: 0 }}>载货</Tag> : <Tag style={{ margin: 0 }}>-</Tag>;
                if (!hasEditPermission) return tag;
                if (ferryToggling === r.id) return <Tag style={{ margin: 0 }}>...</Tag>;
                const items = [
                    { key: 'true', label: <Tag color="red" style={{ margin: 0, cursor: 'pointer' }}>空飞</Tag>, disabled: v === true },
                    { key: 'false', label: <Tag color="green" style={{ margin: 0, cursor: 'pointer' }}>载货</Tag>, disabled: v === false },
                    { key: 'null', label: <Tag style={{ margin: 0, cursor: 'pointer' }}>未知</Tag>, disabled: v == null },
                ];
                const onClick = ({ key }) => {
                    const val = key === 'true' ? true : key === 'false' ? false : null;
                    handleToggleFerry(r, val);
                };
                return (
                    <Dropdown menu={{ items, onClick }} trigger={['click']} placement="bottom">
                        <span style={{ cursor: 'pointer' }}>{tag}</span>
                    </Dropdown>
                );
            },
        },
        {
            title: '邮件时间 (UTC)', dataIndex: 'email_date', width: 118, align: 'center',
            render: (v) => v ? <span style={{ fontFamily: 'Courier New, monospace', fontSize: 12 }}>{dayjs.utc(v).format('MM-DD HH:mm')}</span> : <Typography.Text type="secondary">-</Typography.Text>,
        },
        { title: '加油前(kg)', dataIndex: 'previous_fuel_kg', width: 88, align: 'right', render: fmtFuelWithManual('previous_fuel_kg') },
        { title: '加油(kg)', dataIndex: 'fuel_uplift_kg', width: 88, align: 'right', render: fmtFuelWithManual('fuel_uplift_kg') },
        {
            title: 'OUT', children: [
                { title: '时间 (UTC)', dataIndex: 'out_time', width: 86, align: 'center', render: fmtTimeWithSource('out_time_source') },
                { title: '油量', dataIndex: 'out_fuel_kg', width: 74, align: 'right', render: fmtFuelWithManual('out_fuel_kg') },
            ],
        },
        {
            title: 'OFF', children: [
                { title: '时间 (UTC)', dataIndex: 'off_time', width: 86, align: 'center', render: fmtTimeWithSource('off_time_source') },
                { title: '油量', dataIndex: 'off_fuel_kg', width: 74, align: 'right', render: fmtFuelWithManual('off_fuel_kg') },
            ],
        },
        {
            title: 'ON', children: [
                { title: '时间 (UTC)', dataIndex: 'on_time', width: 86, align: 'center', render: fmtTimeWithSource('on_time_source') },
                { title: '油量', dataIndex: 'on_fuel_kg', width: 74, align: 'right', render: fmtFuelWithManual('on_fuel_kg') },
            ],
        },
        {
            title: 'IN', children: [
                { title: '时间 (UTC)', dataIndex: 'in_time', width: 86, align: 'center', render: fmtTimeWithSource('in_time_source') },
                { title: '油量', dataIndex: 'in_fuel_kg', width: 74, align: 'right', render: fmtFuelWithManual('in_fuel_kg') },
            ],
        },
        {
            title: 'BH油耗', dataIndex: 'block_fuel_kg', width: 78, align: 'right',
            render: (v) => v != null ? <Typography.Text strong style={{ color: '#722ed1' }}>{fmtNum(v)}</Typography.Text> : <Typography.Text type="secondary">-</Typography.Text>,
        },
        {
            title: 'FH油耗', dataIndex: 'flight_fuel_kg', width: 78, align: 'right',
            render: (v) => v != null ? <Typography.Text strong style={{ color: '#eb2f96' }}>{fmtNum(v)}</Typography.Text> : <Typography.Text type="secondary">-</Typography.Text>,
        },
        {
            title: '航班关联', dataIndex: 'match_status', width: 90, align: 'center',
            render: (v, r) => {
                const m = MATCH_STATUS_MAP[v] || { color: 'default', label: v };
                return (
                    <Space size={4}>
                        <Tag color={m.color} style={{ margin: 0 }} icon={v === 'auto_matched' ? <LinkOutlined /> : v === 'unmatched' ? <QuestionCircleOutlined /> : null}>{m.label}</Tag>
                        {r.has_duplicate_match && (
                            <Tooltip title="该航班被多条燃油记录关联，请检查是否存在重复匹配">
                                <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 14 }} />
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: '完整', dataIndex: 'data_completeness', width: 62, align: 'center',
            render: (v) => {
                const m = COMPLETENESS_MAP[v] || { color: 'default', label: v };
                return <Tag color={m.color} style={{ margin: 0 }} icon={v === 'complete' ? <CheckCircleOutlined /> : <WarningOutlined />}>{m.label}</Tag>;
            },
        },
        {
            title: '状态', dataIndex: 'status', width: 62, align: 'center',
            render: (v) => {
                const m = STATUS_MAP[v] || { color: 'default', label: v };
                return <Tag color={m.color} style={{ margin: 0 }}>{m.label}</Tag>;
            },
        },
        {
            title: '操作', width: hasEditPermission ? 78 : 40, fixed: 'right', align: 'center',
            render: (_, r) => (
                <Space size={0}>
                    <Tooltip title="处理"><Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setDetailRecord(r)} /></Tooltip>
                    {hasEditPermission && <>
                        <Tooltip title="删除"><Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete([r.id])} /></Tooltip>
                    </>}
                </Space>
            ),
        },
    ];

    const diagnosticStatusMap = {
        ok: { color: 'green', label: '已入库' },
        pending: { color: 'blue', label: '处理中' },
        blocked: { color: 'orange', label: '卡住' },
        failed: { color: 'red', label: '失败' },
        unknown: { color: 'default', label: '未知' },
    };

    const emailDiagnosticColumns = [
        {
            title: '邮件时间 (UTC)',
            dataIndex: 'date',
            width: 130,
            render: (v) => v ? dayjs.utc(v).format('MM-DD HH:mm') : '-',
        },
        {
            title: '主题',
            dataIndex: 'subject',
            ellipsis: true,
            render: (v, r) => (
                <Space direction="vertical" size={0}>
                    <Typography.Text strong ellipsis style={{ maxWidth: 320 }}>{v || '-'}</Typography.Text>
                    <Typography.Text type="secondary" ellipsis style={{ maxWidth: 320, fontSize: 12 }}>{r.from_address || '-'}</Typography.Text>
                </Space>
            ),
        },
        {
            title: '邮件状态',
            dataIndex: 'status',
            width: 105,
            render: (v) => <Tag>{v || '-'}</Tag>,
        },
        {
            title: '分类',
            dataIndex: 'categories',
            width: 190,
            render: (categories = []) => {
                if (!categories.length) return <Typography.Text type="secondary">未分类</Typography.Text>;
                return (
                    <Space size={[0, 4]} wrap>
                        {categories.map(c => (
                            <Tag key={`${c.category_id}-${c.score}`} color={c.category_id === 'fuel_report' ? 'purple' : 'default'}>
                                {c.category_id}:{c.score}
                            </Tag>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: '诊断',
            dataIndex: 'diagnosis',
            width: 260,
            render: (v, r) => {
                const m = diagnosticStatusMap[r.diagnosis_status] || diagnosticStatusMap.unknown;
                return (
                    <Space direction="vertical" size={2}>
                        <Tag color={m.color} style={{ margin: 0 }}>{m.label}</Tag>
                        <Typography.Text type={r.diagnosis_status === 'ok' ? 'secondary' : 'warning'}>{v}</Typography.Text>
                    </Space>
                );
            },
        },
        {
            title: '报文',
            dataIndex: 'fuel_report_ids',
            width: 80,
            align: 'center',
            render: (ids = []) => ids.length ? <Tag color="green">{ids.length} 条</Tag> : <Typography.Text type="secondary">-</Typography.Text>,
        },
        {
            title: '操作',
            width: 95,
            align: 'center',
            render: (_, r) => (
                <Button
                    size="small"
                    type="link"
                    loading={emailReprocessingId === r.raw_email_id}
                    disabled={emailReprocessingId !== null || r.diagnosis_status === 'ok'}
                    onClick={() => handleReprocessEmail(r.raw_email_id)}
                >
                    重新解析
                </Button>
            ),
        },
    ];

    const inputStyle = { width: '100%' };
    const filterLabel = (text) => <Typography.Text type="secondary" style={{ fontSize: 12, marginBottom: 2, display: 'block' }}>{text}</Typography.Text>;

    // 统计条：已匹配 / 数据不完整用 v3 列表接口的全筛选范围聚合（不受分页影响）；
    // 重复关联仍基于已加载记录（后端只对当页做 dup 检测）
    const matchedCount = aggregates?.matched_count ?? data.filter(r => r.match_status !== 'unmatched').length;
    const partialCount = aggregates?.partial_count ?? data.filter(r => r.data_completeness === 'partial').length;
    const duplicateMatchCount = data.filter(r => r.has_duplicate_match).length;
    const totalUpliftKg = aggregates?.fuel_uplift_kg ?? null;

    // 筛选摘要（供 CollapsibleFilterBar 摘要栏展示）
    const filterSummaryItems = useMemo(() => {
        const items = [];
        const multiLabel = (csv, map) => String(csv).split(',').filter(Boolean)
            .map(v => map[v]?.label || v).join('、');
        const dr = filters.localDateRange;
        if (dr && dr[0] && dr[1]) {
            const fmt = (d) => (typeof d === 'string' ? dayjs(d) : d).format('MM-DD');
            items.push({ label: '日期', value: `${fmt(dr[0])} ~ ${fmt(dr[1])}` });
        }
        if (filters.flight_number) items.push({ label: '航班号', value: filters.flight_number });
        if (filters.aircraft_reg) items.push({ label: '机号', value: filters.aircraft_reg.split(',').join('、') });
        if (filters.operator) items.push({ label: '航司', value: filters.operator });
        if (filters.origin) items.push({ label: '始发', value: filters.origin });
        if (filters.dest) items.push({ label: '目的', value: filters.dest });
        if (filters.match_status) items.push({ label: '匹配', value: multiLabel(filters.match_status, MATCH_STATUS_MAP) });
        if (filters.data_completeness) items.push({ label: '完整度', value: multiLabel(filters.data_completeness, COMPLETENESS_MAP) });
        if (filters.message_type) items.push({ label: '报文', value: multiLabel(filters.message_type, MSG_TYPE_MAP) });
        return items;
    }, [filters]);

    return (
        <div className="fuel-report-list-panel">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: 4, height: 24, background: '#1890ff', borderRadius: 2, marginRight: 12 }} />
                    <Typography.Title level={4} style={{ margin: 0 }}>航班燃油明细</Typography.Title>
                    <Typography.Text type="secondary" style={{ marginLeft: 12, fontSize: 13 }}>
                        邮件解析的燃油过程数据（GRBU / Push Back Report）
                    </Typography.Text>
                </div>
                <Space wrap>
                    <Button icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
                        导出 Excel
                    </Button>
                    {hasEditPermission && <>
                        <Button icon={<LinkOutlined />} onClick={() => handleBatchRematch(null)}>
                            重新匹配
                        </Button>
                        <Button icon={<ReloadOutlined />} loading={backfillingInFuel} onClick={handleBackfillInFuel}>
                            回填 IN 油量
                        </Button>
                        <Button icon={<HistoryOutlined />} onClick={() => { setReprocessOpen(true); setReprocessResult(null); }}>
                            历史回扫
                        </Button>
                        <Button icon={<MailOutlined />} onClick={openEmailDiagnostics}>
                            邮件诊断
                        </Button>
                    </>}
                </Space>
            </div>

            <CollapsibleFilterBar
                summaryItems={filterSummaryItems}
                onReset={handleReset}
                drawerTitle="航班燃油明细 - 筛选条件"
                quickControls={
                    <>
                        <Input
                            placeholder="航班号"
                            size="small"
                            style={{ width: 170, height: 26 }}
                            value={filters.flight_number}
                            onChange={(e) => {
                                const v = e.target.value;
                                const next = { ...filters, flight_number: v };
                                setFilters(next);
                                runQuickSearch(next);
                            }}
                            allowClear
                        />
                        <Select
                            mode="tags"
                            placeholder="机号(可多选/模糊)"
                            size="small"
                            popupMatchSelectWidth={false}
                            value={filters.aircraft_reg ? filters.aircraft_reg.split(',') : []}
                            onChange={(vals) => {
                                const next = { ...filters, aircraft_reg: (vals || []).map(v => v.trim()).filter(Boolean).join(',') };
                                setFilters(next);
                                runQuickSearch(next, { immediate: true });
                            }}
                            options={regOptions.map(r => ({ value: r, label: r }))}
                            maxTagCount="responsive"
                            tokenSeparators={[',', ' ']}
                            allowClear
                            style={{ minWidth: 160, maxWidth: 260 }}
                        />
                        <Select
                            placeholder="匹配状态"
                            size="small"
                            popupMatchSelectWidth={false}
                            style={{ minWidth: 130, maxWidth: 160 }}
                            value={filters.match_status}
                            onChange={(v) => {
                                const next = { ...filters, match_status: v };
                                setFilters(next);
                                runQuickSearch(next, { immediate: true });
                            }}
                            allowClear
                            options={[{ value: 'auto_matched', label: '自动匹配' }, { value: 'manual_matched', label: '人工匹配' }, { value: 'unmatched', label: '未匹配' }]}
                        />
                    </>
                }
            >
                <Row gutter={[12, 8]}>
                    <Col xs={24} sm={12} md={8}>
                        {filterLabel('航班日期')}
                        <RangePicker size="small" style={inputStyle} value={filters.localDateRange}
                            onChange={(v) => setFilters(f => ({ ...f, localDateRange: v }))} placeholder={['起始', '截止']} allowClear />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        {filterLabel('航班号（模糊）')}
                        <Input size="small" placeholder="如 CT0166" value={filters.flight_number} onChange={e => setFilters(f => ({ ...f, flight_number: e.target.value }))} onPressEnter={handleSearch} style={inputStyle} allowClear />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        {filterLabel('机号（可多选，支持模糊，如 WFF）')}
                        <Select
                            mode="tags"
                            size="small"
                            placeholder="选择或输入机号"
                            popupMatchSelectWidth={false}
                            value={filters.aircraft_reg ? filters.aircraft_reg.split(',') : []}
                            onChange={(vals) => setFilters(f => ({ ...f, aircraft_reg: (vals || []).map(v => v.trim()).filter(Boolean).join(',') }))}
                            options={regOptions.map(r => ({ value: r, label: r }))}
                            maxTagCount="responsive"
                            tokenSeparators={[',', ' ']}
                            allowClear
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        {filterLabel('航司/运营人（模糊）')}
                        <Select
                            size="small"
                            showSearch
                            placeholder="全部"
                            value={filters.operator || undefined}
                            onChange={(v) => setFilters(f => ({ ...f, operator: v || '' }))}
                            options={operatorOptions.map(o => ({ value: o, label: o }))}
                            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                            allowClear
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        {filterLabel('始发站（模糊）')}
                        <Input size="small" placeholder="IATA/ICAO，如 CTU" value={filters.origin} onChange={e => setFilters(f => ({ ...f, origin: e.target.value }))} onPressEnter={handleSearch} style={inputStyle} allowClear />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        {filterLabel('目的站（模糊）')}
                        <Input size="small" placeholder="IATA/ICAO，如 TFU" value={filters.dest} onChange={e => setFilters(f => ({ ...f, dest: e.target.value }))} onPressEnter={handleSearch} style={inputStyle} allowClear />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        {filterLabel('匹配状态（可多选）')}
                        <Select size="small" mode="multiple" placeholder="全部" value={filters.match_status ? filters.match_status.split(',') : []} onChange={vals => setFilters(f => ({ ...f, match_status: (vals || []).join(',') || undefined }))} allowClear style={inputStyle}
                            options={[{ value: 'auto_matched', label: '自动匹配' }, { value: 'manual_matched', label: '人工匹配' }, { value: 'unmatched', label: '未匹配' }]} />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        {filterLabel('完整度（可多选）')}
                        <Select size="small" mode="multiple" placeholder="全部" value={filters.data_completeness ? filters.data_completeness.split(',') : []} onChange={vals => setFilters(f => ({ ...f, data_completeness: (vals || []).join(',') || undefined }))} allowClear style={inputStyle}
                            options={[{ value: 'complete', label: '完整' }, { value: 'partial', label: '部分' }]} />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        {filterLabel('报文类型（可多选）')}
                        <Select size="small" mode="multiple" placeholder="全部" value={filters.message_type ? filters.message_type.split(',') : []} onChange={vals => setFilters(f => ({ ...f, message_type: (vals || []).join(',') || undefined }))} allowClear style={inputStyle}
                            options={[{ value: 'GRBU_SUMMARY', label: 'GRBU' }, { value: 'PUSH_BACK_REPORT', label: 'PushBack' }, { value: 'MERGED', label: '合并' }, { value: 'OTHER', label: '其他' }]} />
                    </Col>
                    <Col xs={24} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Space>
                            <Button size="small" type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
                            <Button size="small" icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
                        </Space>
                    </Col>
                </Row>
            </CollapsibleFilterBar>

            <div style={{ marginBottom: 8, padding: '6px 12px', background: '#fafafa', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, flexShrink: 0 }}>
                <Space size={20} wrap>
                    <span>已加载 <Typography.Text strong>{data.length}</Typography.Text> / {total} 条</span>
                    {totalUpliftKg != null && (
                        <Tooltip title="当前筛选范围内全部记录的加油量合计">
                            <span>加油总量 <Typography.Text strong style={{ fontFamily: 'Courier New, monospace' }}>{fmtNum(totalUpliftKg)}</Typography.Text> kg</span>
                        </Tooltip>
                    )}
                    <Tooltip title="当前筛选范围内全部记录"><span>已匹配 <Typography.Text strong style={{ color: '#52c41a' }}>{matchedCount}</Typography.Text></span></Tooltip>
                    <Tooltip title="当前筛选范围内全部记录"><span>数据不完整 <Typography.Text strong style={{ color: '#faad14' }}>{partialCount}</Typography.Text></span></Tooltip>
                    {duplicateMatchCount > 0 && (
                        <span><ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 4 }} />重复关联 <Typography.Text strong style={{ color: '#ff4d4f' }}>{duplicateMatchCount}</Typography.Text></span>
                    )}
                </Space>
                {selectedRowKeys.length > 0 && hasEditPermission && (
                    <Space>
                        <Typography.Text type="secondary">已选 {selectedRowKeys.length} 条</Typography.Text>
                        <Button size="small" icon={<LinkOutlined />} onClick={() => handleBatchRematch(selectedRowKeys)}>重新匹配</Button>
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(selectedRowKeys)}>批量删除</Button>
                        <Button size="small" type="link" onClick={() => setSelectedRowKeys([])}>取消选择</Button>
                    </Space>
                )}
            </div>

            <div className="fuel-report-list-table-wrap" ref={tableWrapRef}>
                <Table className="fuel-detail-table" columns={columns} dataSource={data} rowKey="id" size="small" bordered
                    pagination={false}
                    onScroll={handleTableScroll}
                    scroll={{ x: 2000, y: tableScrollY }}
                    loading={loading}
                    rowSelection={hasEditPermission ? {
                        selectedRowKeys,
                        onChange: setSelectedRowKeys,
                        columnWidth: 40,
                    } : undefined}
                    rowClassName={(r) => r.data_completeness === 'partial' ? 'fuel-row-partial' : ''}
                    footer={
                        total > 0
                            ? () => (
                                <div className="fuel-report-scroll-footer">
                                    {loadingMore ? (
                                        <Spin size="small" />
                                    ) : hasMore ? (
                                        <Button type="link" onClick={loadMore}>
                                            向下滚动或点击加载更多
                                        </Button>
                                    ) : (
                                        <Typography.Text type="secondary">已加载全部 {total} 条</Typography.Text>
                                    )}
                                </div>
                            )
                            : undefined
                    }
                />
            </div>

            <FuelDetailDrawer record={detailRecord} open={!!detailRecord} onClose={() => setDetailRecord(null)} onChanged={handleDetailChanged} hasEditPermission={hasEditPermission} />

            <Modal title={<><HistoryOutlined style={{ marginRight: 8 }} />历史邮件回扫</>} open={reprocessOpen}
                onCancel={() => { stopPolling(); setReprocessing(false); setReprocessOpen(false); setReprocessResult(null); }}
                footer={[
                    <Button key="cancel" onClick={() => { stopPolling(); setReprocessing(false); setReprocessOpen(false); setReprocessResult(null); }}>关闭</Button>,
                    <Button key="run" type="primary" loading={reprocessing} onClick={handleReprocess}
                        disabled={!reprocessRange}>开始回扫</Button>,
                ]}
                destroyOnHidden>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <div>
                        <Typography.Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
                            选择邮件日期范围（最多 180 天），系统将在后台异步扫描
                        </Typography.Text>
                        <RangePicker style={{ width: '100%' }} value={reprocessRange}
                            onChange={setReprocessRange} placeholder={['起始日期', '截止日期']} />
                    </div>
                    {reprocessResult && (
                        <Card size="small" style={{
                            background: reprocessResult.status === 'failed' ? '#fff2f0' : reprocessResult.status === 'completed' ? '#f6ffed' : '#f0f5ff',
                            borderColor: reprocessResult.status === 'failed' ? '#ffccc7' : reprocessResult.status === 'completed' ? '#b7eb8f' : '#adc6ff',
                        }}>
                            {reprocessResult.status === 'running' && reprocessResult.scanned > 0 && (
                                <div style={{ marginBottom: 12 }}>
                                    <Progress
                                        percent={Math.round((reprocessResult.processed || 0) / reprocessResult.scanned * 100)}
                                        size="small"
                                        status="active"
                                        format={() => `${reprocessResult.processed || 0} / ${reprocessResult.scanned}`}
                                    />
                                </div>
                            )}
                            {reprocessResult.status === 'completed' && (
                                <div style={{ textAlign: 'center', marginBottom: 8 }}>
                                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18, marginRight: 6 }} />
                                    <Typography.Text strong>回扫完成</Typography.Text>
                                </div>
                            )}
                            <Row gutter={16}>
                                <Col span={6}>
                                    <div style={{ textAlign: 'center', padding: '8px 0' }}>
                                        <div style={{ fontSize: 22, fontWeight: 600, fontFamily: 'Courier New, monospace' }}>{reprocessResult.scanned ?? '-'}</div>
                                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>扫描邮件</div>
                                    </div>
                                </Col>
                                <Col span={6}>
                                    <div style={{ textAlign: 'center', padding: '8px 0' }}>
                                        <div style={{ fontSize: 22, fontWeight: 600, color: '#52c41a', fontFamily: 'Courier New, monospace' }}>{reprocessResult.created ?? '-'}</div>
                                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>新入库</div>
                                    </div>
                                </Col>
                                <Col span={6}>
                                    <div style={{ textAlign: 'center', padding: '8px 0' }}>
                                        <div style={{ fontSize: 22, fontWeight: 600, color: '#faad14', fontFamily: 'Courier New, monospace' }}>{reprocessResult.skipped ?? '-'}</div>
                                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>跳过(重复)</div>
                                    </div>
                                </Col>
                                <Col span={6}>
                                    <div style={{ textAlign: 'center', padding: '8px 0' }}>
                                        <div style={{ fontSize: 22, fontWeight: 600, color: '#ff4d4f', fontFamily: 'Courier New, monospace' }}>{reprocessResult.failed ?? '-'}</div>
                                        <div style={{ color: '#8c8c8c', fontSize: 12 }}>失败</div>
                                    </div>
                                </Col>
                            </Row>
                        </Card>
                    )}
                </Space>
            </Modal>

            <Modal
                title={<><MailOutlined style={{ marginRight: 8 }} />邮件解析诊断</>}
                open={emailDiagnosticsOpen}
                onCancel={() => setEmailDiagnosticsOpen(false)}
                width={1100}
                footer={[
                    <Button key="refresh" icon={<ReloadOutlined />} loading={emailDiagnosticsLoading} onClick={fetchEmailDiagnostics}>刷新</Button>,
                    <Button key="close" onClick={() => setEmailDiagnosticsOpen(false)}>关闭</Button>,
                ]}
                destroyOnHidden
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Alert
                        type="info"
                        showIcon
                        message={`最近 7 天疑似燃油邮件：${emailDiagnostics.summary?.total ?? 0}，已入库 ${emailDiagnostics.summary?.generated ?? 0}，处理中 ${emailDiagnostics.summary?.pending ?? 0}，卡住/失败 ${emailDiagnostics.summary?.blocked ?? 0}`}
                    />
                    <Table
                        size="small"
                        rowKey="raw_email_id"
                        columns={emailDiagnosticColumns}
                        dataSource={emailDiagnostics.items || []}
                        loading={emailDiagnosticsLoading}
                        pagination={{ pageSize: 10, showSizeChanger: false }}
                        scroll={{ x: 1050, y: 440 }}
                    />
                </Space>
            </Modal>

            <style>{`
                .fuel-report-list-panel {
                    height: calc(100dvh - 196px);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    padding: 12px 24px 0;
                }
                .fuel-report-list-panel > *:not(.fuel-report-list-table-wrap) {
                    flex-shrink: 0;
                }
                .fuel-report-list-table-wrap {
                    flex: 1;
                    min-height: 0;
                    overflow: hidden;
                }
                .fuel-report-scroll-footer {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-height: 32px;
                }
                .fuel-row-partial { background: #fffbe6 !important; }
                .fuel-row-partial:hover > td { background: #fff7cc !important; }
                .fuel-compare-diff > td { background: #fff7e6 !important; }
                .fuel-compare-diff:hover > td { background: #ffe7ba !important; }
                .fuel-detail-table .ant-table-header {
                    z-index: 3;
                }
                .fuel-detail-table .ant-table-thead > tr > th {
                    background: #f5f7fa !important;
                    font-size: 12px;
                    color: #606266;
                    white-space: nowrap;
                }
                .fuel-detail-table .ant-table-tbody > tr > td {
                    font-size: 13px;
                    padding: 6px 8px !important;
                }
                .fuel-detail-table .ant-table-thead > tr:first-child > th {
                    border-bottom: 2px solid #e6e8eb;
                }
                .fuel-report-editor-form .ant-row {
                    min-width: 0;
                }
                .fuel-report-editor-form .ant-col {
                    min-width: 0;
                }
                .fuel-report-editor-form .ant-form-item {
                    min-width: 0;
                }
                .fuel-report-editor-form .ant-form-item-control,
                .fuel-report-editor-form .ant-form-item-control-input,
                .fuel-report-editor-form .ant-form-item-control-input-content {
                    min-width: 0;
                    width: 100%;
                }
                .fuel-report-editor-form .ant-input,
                .fuel-report-editor-form .ant-input-number,
                .fuel-report-editor-form .ant-picker {
                    width: 100% !important;
                    max-width: 100%;
                    min-width: 0;
                }
            `}</style>
        </div>
    );
};

// ─── Main Page (Tabs wrapper) ──────────────────────────────────────
const FuelReportPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab');
    const activeKey = FUEL_REPORT_TAB_KEYS.includes(tabFromUrl) ? tabFromUrl : 'list';

    useEffect(() => {
        if (tabFromUrl !== activeKey) {
            const next = new URLSearchParams(searchParams);
            next.set('tab', activeKey);
            setSearchParams(next, { replace: true });
        }
    }, [activeKey, tabFromUrl, searchParams, setSearchParams]);

    const handleTabChange = (key) => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', key);
        setSearchParams(next);
    };

    const tabItems = [
        {
            key: 'list',
            label: <Space size={4}><EditOutlined />航班燃油明细</Space>,
            children: <FuelReportListPanel />,
        },
        {
            key: 'image',
            label: <Space size={4}><EyeOutlined />加油单图片识别</Space>,
            children: (
                <div style={{ padding: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                        <div style={{ width: 4, height: 24, background: '#722ed1', borderRadius: 2, marginRight: 12 }} />
                        <Typography.Title level={4} style={{ margin: 0 }}>加油单图片识别</Typography.Title>
                        <Typography.Text type="secondary" style={{ marginLeft: 12, fontSize: 13 }}>
                            上传机务/运行人员拍摄的加油单图片，AI 自动识别并匹配燃油报文
                        </Typography.Text>
                    </div>
                    <FuelFormImageList />
                </div>
            ),
        },
        {
            key: 'flight-log',
            label: <Space size={4}><FileTextOutlined />{'flight log\u8bc6\u522b'}</Space>,
            children: <FlightLogRecognitionPanel />,
        },
    ];

    return (
        <Tabs
            activeKey={activeKey}
            onChange={handleTabChange}
            items={tabItems}
            size="large"
            tabBarStyle={{
                marginBottom: 0,
                padding: '12px 24px 0',
                background: '#fff',
                borderBottom: '1px solid #f0f0f0',
            }}
            destroyInactiveTabPane={false}
        />
    );
};

export default FuelReportPage;
